/**
 * Resolução de feature da clínica logada — fonte ÚNICA da regra (spec 15 / R3).
 *
 * Antes desta spec a regra vivia em três lugares, com semânticas divergentes:
 *
 *   - permissionsHelp.js (`showFeatureByKey`): só checava se a clínica tem a
 *     feature. Lista de profissionais ignorada → lista vazia liberava para TODOS.
 *   - useConsultaData.ts (react-apps/attendance/shell): reimplementava a leitura
 *     do localStorage por conta própria.
 *   - DoctorAssistantAI.vue (2 cópias) e Attendance/Header/index.vue: exigiam
 *     `professionals.some(p => p.person_id === user.id)` → lista vazia negava
 *     para TODOS.
 *
 * Ou seja: o mesmo dado (`clinic_features[].professionals`) significava coisas
 * opostas dependendo de quem lia. O backend agora carrega o significado em
 * `clinic_features[].rollout_mode` (spec 15 / R1), e este módulo é a única
 * tradução dele no frontend. A tabela abaixo espelha
 * App\Enums\Feature\FeatureRolloutMode::grantsAccess — divergir aqui é divergir
 * na Consulta 3.0 e no DAAI.
 *
 *   all        todos os profissionais da clínica (a lista é ignorada)
 *   allowlist  somente os profissionais vinculados
 *   denylist   todos, exceto os profissionais vinculados
 *
 * Consumido tanto pelo Vue (via permissionsHelp.js) quanto pelos react-apps
 * (import direto por `@/utils/featureGating`).
 */

export const ROLLOUT_ALL = 'all'
export const ROLLOUT_ALLOWLIST = 'allowlist'
export const ROLLOUT_DENYLIST = 'denylist'

const readJson = key => {
  try {
    return JSON.parse(localStorage.getItem(key) || 'null')
  } catch {
    return null
  }
}

export const readClinic = () => readJson('clinic')

export const readUser = () => readJson('user')

const featuresOf = clinic =>
  Array.isArray(clinic?.clinic_features) ? clinic.clinic_features : []

/**
 * Todos os vínculos clínica↔feature que casam por nome OU key.
 *
 * Aceitar os dois não é preguiça: metade do código legado identifica feature por
 * `feature.name` (string em português, ex. 'Resultado Doctor Assistant') e a
 * metade nova por `feature.key` (slug, ex. 'attendance-v3'). Unificar os
 * chamadores é trabalho separado; até lá o helper resolve os dois.
 *
 * Devolve LISTA, não o primeiro: não há unique em clinic_features
 * (clinic_id, feature_id), então a mesma feature pode aparecer em mais de uma
 * linha. O backend, nesse caso, tira o modo da linha mais antiga e UNE os
 * person_ids (ClinicFeatureRepository::rolloutStateForClinic) — resolver só pela
 * primeira linha faria a UI negar um acesso que a API concede.
 */
const matchingClinicFeatures = ({ clinic, name, key }) => {
  const hasKey = key !== undefined && key !== null
  const hasName = name !== undefined && name !== null

  // Sem critério não há match. Sem esta guarda, `{}` compararia
  // `undefined === undefined` e casaria com qualquer linha cujo `feature` fosse
  // null — falso positivo silencioso.
  if (!hasKey && !hasName) return []

  return featuresOf(clinic).filter(cf =>
    hasKey ? cf?.feature?.key === key : cf?.feature?.name === name
  )
}

/**
 * Modo efetivo: o da primeira linha que declare um modo válido — paridade com o
 * backend, que usa a linha mais antiga. `legacyMode` é o que assumir quando
 * nenhuma linha traz `rollout_mode`, o que acontece com payload de sessão
 * anterior ao deploy da spec 15 (o localStorage se atualiza no próximo
 * check-user). Cada chamador declara o seu, porque o comportamento pré-spec-15
 * era diferente por feature.
 */
const rolloutModeOf = (clinicFeatures, legacyMode) => {
  const declared = clinicFeatures
    .map(cf => cf?.rollout_mode)
    .find(
      mode =>
        mode === ROLLOUT_ALL ||
        mode === ROLLOUT_ALLOWLIST ||
        mode === ROLLOUT_DENYLIST
    )

  return declared ?? legacyMode
}

/** União dos vínculos de todas as linhas da feature — paridade com o backend. */
const isLinked = (clinicFeatures, personId) => {
  if (!personId) return false

  return clinicFeatures.some(cf => {
    const professionals = cf?.professionals

    return (
      Array.isArray(professionals) &&
      professionals.some(p => p?.person_id === personId)
    )
  })
}

/**
 * A regra. Espelha FeatureRolloutMode::grantsAccess no backend.
 */
const grantsAccess = (mode, linked) => {
  if (mode === ROLLOUT_ALLOWLIST) return linked
  if (mode === ROLLOUT_DENYLIST) return !linked
  return true
}

/**
 * A clínica logada tem a feature habilitada?
 *
 * Só sobre a CLÍNICA — ignora `rollout_mode`, porque a pergunta é sobre a
 * clínica, não sobre uma pessoa. É o que a maioria dos gates de menu/rota quer
 * saber, e é a paridade exata do comportamento anterior de
 * showFeature/showFeatureByKey.
 *
 * O JSDoc não é enfeite: sem ele o TypeScript infere o parâmetro a partir do
 * default `= {}` e conclui `{ clinic?: any }`, quebrando todo consumidor .ts
 * que passe `name` ou `key`.
 *
 * @param {{ name?: string, key?: string, clinic?: any }} [options]
 * @returns {boolean}
 */
export const clinicHasFeature = ({ name, key, clinic = readClinic() } = {}) =>
  matchingClinicFeatures({ clinic, name, key }).length > 0

/**
 * O profissional logado tem acesso à feature nesta clínica?
 *
 * Aplica o `rollout_mode`. Use quando o gate é sobre uma PESSOA (habilitar um
 * botão para o médico logado), não sobre a clínica.
 *
 * `legacyMode` é o modo assumido quando o payload não traz `rollout_mode` —
 * localStorage de uma sessão anterior ao deploy da spec 15. O default é
 * `allowlist` (fail-closed) porque toda feature que era gateada por profissional
 * antes da spec praticava exatamente essa regra: `hasFeature &&
 * professionals.some(...)`, com lista vazia negando. Uma feature cujo gate era
 * por CLÍNICA e virou por profissional agora (caso do `attendance-v3`) precisa
 * passar `legacyMode: ROLLOUT_ALL` explicitamente — senão perderia acesso na
 * janela entre o deploy e o próximo check-user.
 *
 * Sem usuário identificável, o profissional é tratado como NÃO vinculado e o modo
 * decide. Isso preserva exatamente o comportamento pré-spec-15 do DAAI (que
 * escondia o gravador quando não conseguia casar `person_id` com `user.id`) e
 * falha para o lado seguro em cada modo: `allowlist` nega, `denylist` libera —
 * coerente com "todos, exceto os da lista" —, `all` libera.
 */
export const professionalHasFeature = ({
  name,
  key,
  legacyMode = ROLLOUT_ALLOWLIST,
  clinic = readClinic(),
  user = readUser()
} = {}) => {
  const clinicFeatures = matchingClinicFeatures({ clinic, name, key })

  if (clinicFeatures.length === 0) return false

  return grantsAccess(
    rolloutModeOf(clinicFeatures, legacyMode),
    isLinked(clinicFeatures, user?.id)
  )
}

/**
 * Modo de rollout da feature nesta clínica, ou null se a feature não está
 * habilitada. Exposto para debug/telemetria — a decisão de acesso deve sair de
 * professionalHasFeature, nunca de comparação manual do modo no chamador.
 */
export const featureRolloutMode = ({
  name,
  key,
  legacyMode = ROLLOUT_ALL,
  clinic = readClinic()
} = {}) => {
  const clinicFeatures = matchingClinicFeatures({ clinic, name, key })

  return clinicFeatures.length > 0
    ? rolloutModeOf(clinicFeatures, legacyMode)
    : null
}
